import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db, getPublicChatMessages } from '@siaedts/shared/lib/supabase';
import { localMessages, queueSyncOperation } from '@siaedts/shared';

type ChatMessage = { id: string; message: string; status: string; reply_message: string | null; sender: string; created_at: string };
type ChatSession = { conversationId: string; chatToken: string; name: string; email: string; phone: string };

function readChatSession(): ChatSession | null {
  const raw = localStorage.getItem('siaedts.live-chat');
  if (!raw) return null;
  try { return JSON.parse(raw) as ChatSession; } catch { return null; }
}

export function LiveChatWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<ChatSession | null>(() => readChatSession());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [form, setForm] = useState({ name: session?.name ?? '', email: session?.email ?? '', phone: session?.phone ?? '', message: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!session) return;
    let active = true;
    const refresh = async () => {
      try {
        const nextMessages = await getPublicChatMessages(session.conversationId, session.chatToken);
        if (active) {
          setMessages(nextMessages);
          const lastActivity = nextMessages[nextMessages.length - 1]?.created_at;
          const inactive = lastActivity && Date.now() - new Date(lastActivity).getTime() >= 3 * 60 * 1000;
          setClosed(nextMessages.some((item) => item.status === 'archived') || Boolean(inactive));
        }
      } catch { /* Keep the thread visible while offline. */ }
    };
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, 2000);
    return () => { active = false; window.clearInterval(timer); };
  }, [session]);

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    setSending(true);
    setError('');
    const currentSession = !session || closed ? {
      conversationId: crypto.randomUUID(),
      chatToken: `${crypto.randomUUID()}${crypto.randomUUID()}`,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
    } : session;
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      subject: 'Live website chat',
      message: form.message.trim(),
      conversation_id: currentSession.conversationId,
      chat_token: currentSession.chatToken,
      status: 'unread' as const,
    };

    try {
      await db.contactMessages.create(payload as never);
      localStorage.setItem('siaedts.live-chat', JSON.stringify(currentSession));
      setSession(currentSession);
      setClosed(false);
      setForm({ ...form, message: '' });
    } catch {
      try {
        const localMessage = await localMessages.create(payload);
        await queueSyncOperation({ entity: 'messages', entity_id: localMessage.id, operation: 'create', payload: localMessage });
        localStorage.setItem('siaedts.live-chat', JSON.stringify(currentSession));
        setSession(currentSession);
        setClosed(false);
        setForm({ ...form, message: '' });
      } catch {
        setError(t('widgets.sendError'));
      }
    } finally { setSending(false); }
  }

  return <div className="live-chat-widget">
    {open && <section className="live-chat-panel" aria-label={t('widgets.liveChat')}>
      <div className="live-chat-header"><div><strong>{t('widgets.chatWithSiaedts')}</strong><span>{t('widgets.quickReply')}</span></div><button type="button" onClick={() => setOpen(false)} aria-label={t('widgets.closeChat')}>×</button></div>
      {session && <div className="live-chat-thread">{messages.length === 0 ? <p className="live-chat-empty">{t('widgets.startConversation')}</p> : messages.map((item) => <div className="live-chat-message-group" key={item.id}><div className="live-chat-bubble customer">{item.message}</div>{item.reply_message && <div className="live-chat-bubble agent"><strong>SIAEDTS</strong>{item.reply_message}</div>}</div>)}</div>}
      {closed ? <div className="live-chat-closed"><strong>{t('widgets.chatClosed')}</strong><p>{t('widgets.chatClosedReason')}</p><button type="button" onClick={() => { setClosed(false); setSession(null); setMessages([]); setForm({ name: '', email: '', phone: '', message: '' }); }}>{t('widgets.startNewConversation')}</button></div> : <form onSubmit={sendMessage} className="live-chat-form">{!session && <><label>{t('widgets.yourName')}<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>{t('widgets.email')}<input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label><label>{t('widgets.phoneWhatsapp')}<input required type="tel" placeholder="+224 622 702 486" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label></>}<label>{t('widgets.message')}<textarea required rows={3} value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} /></label>{error && <p className="live-chat-error">{error}</p>}<button className="live-chat-submit" type="submit" disabled={sending}>{sending ? t('widgets.sending') : t('widgets.sendMessage')}</button></form>}
    </section>}
    <button className="live-chat-trigger" type="button" onClick={() => setOpen(!open)} aria-expanded={open}><span className="live-chat-dot" aria-hidden="true" />{open ? t('widgets.closeChat') : t('widgets.chatWithUs')}</button>
  </div>;
}
